import { useEffect, useState } from "react";
import api from "../api/api";

/* ── ADD STATE ─────────────────────────────────────────── */
function AddState({ onAdded }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/state", { name });
      setName("");
      onAdded?.();
    } catch { alert("Failed to add state"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <input className="form-input" placeholder="State name" style={{ flex: 1 }}
        value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && add()} />
      <button className="btn btn-gold btn-sm" onClick={add} disabled={loading || !name.trim()}>
        {loading ? "…" : "Add"}
      </button>
    </div>
  );
}

/* ── ADD CITY ──────────────────────────────────────────── */
function AddCity({ states }) {
  const [stateId, setStateId] = useState("");
  const [name,    setName]    = useState("");
  const [loading, setLoading] = useState(false);

  const add = async () => {
    if (!stateId || !name.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/city", { name, stateId });
      setName("");
    } catch { alert("Failed to add city"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      <select className="form-select" style={{ flex: "0 0 160px" }}
        value={stateId} onChange={e => setStateId(e.target.value)}>
        <option value="">Select state</option>
        {states.map(s => <option key={s.stateId} value={s.stateId}>{s.name}</option>)}
      </select>
      <input className="form-input" placeholder="City name" style={{ flex: 1 }}
        value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && add()} />
      <button className="btn btn-gold btn-sm" onClick={add} disabled={loading || !stateId || !name.trim()}>
        {loading ? "…" : "Add"}
      </button>
    </div>
  );
}

/* ── ADD AREA ──────────────────────────────────────────── */
function AddArea({ states }) {
  const [stateId, setStateId] = useState("");
  const [cities,  setCities]  = useState([]);
  const [cityId,  setCityId]  = useState("");
  const [name,    setName]    = useState("");
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCities = (id) => {
    setStateId(id); setCities([]); setCityId("");
    if (id) api.get(`/public/cities/${id}`).then(res => setCities(res.data)).catch(() => {});
  };

  const add = async () => {
    if (!cityId || !name.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/area", { name, pincode, cityId });
      setName(""); setPincode("");
    } catch { alert("Failed to add area"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <select className="form-select" style={{ flex: "0 0 140px" }}
          value={stateId} onChange={e => loadCities(e.target.value)}>
          <option value="">Select state</option>
          {states.map(s => <option key={s.stateId} value={s.stateId}>{s.name}</option>)}
        </select>
        <select className="form-select" style={{ flex: "0 0 140px" }}
          value={cityId} onChange={e => setCityId(e.target.value)}>
          <option value="">Select city</option>
          {cities.map(c => <option key={c.cityId} value={c.cityId}>{c.name}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="form-input" placeholder="Area name" style={{ flex: 1 }}
          value={name} onChange={e => setName(e.target.value)} />
        <input className="form-input" placeholder="Pincode" style={{ width: 110 }}
          value={pincode} onChange={e => setPincode(e.target.value)} />
        <button className="btn btn-gold btn-sm" onClick={add}
          disabled={loading || !cityId || !name.trim()}>
          {loading ? "…" : "Add"}
        </button>
      </div>
    </div>
  );
}

/* ── LOCATION MANAGER (main export) ───────────────────── */
function LocationManager() {
  const [states,  setStates]  = useState([]);

  const loadStates = () => {
    api.get("/public/states")
      .then(res => setStates(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  useEffect(() => { loadStates(); }, []);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22 }}>

        {/* States */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🗺️ States</div>
            <span className="badge badge-muted">{states.length}</span>
          </div>
          <div className="card-body">
            <AddState onAdded={loadStates} />
            {states.map(s => (
              <div key={s.stateId} style={{
                padding: "9px 0", borderBottom: "1px solid var(--border)",
                fontSize: 14
              }}>
                📍 {s.name}
              </div>
            ))}
            {states.length === 0 && <p className="text-muted fs-13">No states added yet</p>}
          </div>
        </div>

        {/* Cities */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏙️ Add City</div>
          </div>
          <div className="card-body">
            <p className="text-muted fs-12 mb-16">Select a state, then add a city</p>
            <AddCity states={states} />
          </div>
        </div>

        {/* Areas */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📍 Add Area</div>
          </div>
          <div className="card-body">
            <p className="text-muted fs-12 mb-16">Select state → city, then add an area</p>
            <AddArea states={states} />
          </div>
        </div>

      </div>
    </div>
  );
}

export default LocationManager;
